import { ALL_ENTRIES, getBreadcrumbTrail, getEntryByPath } from './registry'
import { BreadcrumbItem, EntityKind, RegistryEntry } from './types'
import { canonicalUrl, toPathname } from './urls'

export interface PublishableUrl {
  path: string
  /** Absolute canonical URL. */
  url: string
  kind: EntityKind
  name: string
  /** ISO date of the last real content change; `undefined` when unknown (omit `<lastmod>` then). */
  lastModified?: string
}

/**
 * Single publication gate. An entry is publishable when it is flagged `published` and, for medical
 * content (`requiresReview`), its review status is `approved`.
 */
export const isPublishable = (entry: RegistryEntry): boolean =>
  entry.published && (!entry.requiresReview || entry.review.reviewStatus === 'approved')

const toPublishableUrl = (entry: RegistryEntry): PublishableUrl => ({
  path: entry.path,
  url: canonicalUrl(entry.path),
  kind: entry.kind,
  name: entry.name,
  lastModified: entry.review.dateModified ?? entry.review.datePublished
})

/**
 * THE list of indexable organic Medleve URLs. Sitemap, footer and internal links must read from here,
 * so a page enters all of them the moment its registry entry becomes publishable — and never before.
 */
export const getPublishableOrganicUrls = (entries: RegistryEntry[] = ALL_ENTRIES): PublishableUrl[] =>
  entries.filter(isPublishable).map(toPublishableUrl)

/** Evaluated once for the built-in registries. */
export const PUBLISHABLE_ORGANIC_URLS: PublishableUrl[] = getPublishableOrganicUrls()

export const isPublishablePath = (path: string): boolean => {
  const entry = getEntryByPath(path)
  return !!entry && isPublishable(entry)
}

/**
 * Href for an internal organic link, or `undefined` when the target is not live.
 * Callers must render plain text when this returns `undefined` (PRD §17/§30: no broken URLs).
 */
export const resolveInternalHref = (path: string): string | undefined =>
  isPublishablePath(path) ? toPathname(path) : undefined

/**
 * Removes the `path` of every crumb whose target is not live. Use the result for BOTH the visual
 * breadcrumb and the BreadcrumbList JSON-LD so they never diverge (PRD §55).
 */
export const resolveBreadcrumbs = (items: BreadcrumbItem[]): BreadcrumbItem[] =>
  items.map(({ name, path }) => {
    const href = path ? resolveInternalHref(path) : undefined
    return href ? { name, path: href } : { name }
  })

/** Resolved breadcrumb trail for a registry path; `[]` for unknown paths and the homepage. */
export const getBreadcrumbsForPath = (path: string): BreadcrumbItem[] => {
  const entry = getEntryByPath(path)
  return entry ? resolveBreadcrumbs(getBreadcrumbTrail(entry)) : []
}
